import { useEffect, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { defineMessages, IntlProvider, useIntl } from 'react-intl';
import { messages as initParamsErrorMessages } from '@onramp/components/InitParamsErrorView';
import { useEffectOnMount } from '@onramp/hooks/useEffectOnMount';
import type { InitParamErrors } from '@onramp/types/initParamErrors';
import { wrapWithCallOnce } from '@onramp/utils/wrapWithCallOnce';
import {
  API_GCO_APPLE_PAY,
  API_GCO_APPLE_PAY_INIT_PARAMS_ERROR,
} from '@onramp/v2/client/views/api-onramp/analytics/events';
import { useLogOnrampEventV2 } from '@onramp/v2/client/views/guest/onramp/analytics/useLogOnrampEventV2';
import {
  OnrampApiLoadErrorEvent,
  sendApiOnrampPostMessage,
} from '@onramp/v2/client/views/guest/onramp/utils/guestPostMessage';
import { useRouter } from 'next/router';
import { snakeCase } from '@cbhq/cds-utils';
import { logMetric, MetricType } from '@cbhq/client-analytics';

type InitParamsErrorPostMessageViewProps = {
  errors: InitParamErrors;
} & Required<Pick<ComponentProps<typeof IntlProvider>, 'locale' | 'messages'>>;

export function InitParamsErrorPostMessageView({
  errors,
  locale,
  messages: providerMessages,
}: InitParamsErrorPostMessageViewProps) {
  const DEFAULT_LOCALE = useRouter().locale;

  useEffectOnMount(() => {
    logInitParamErrorsMetric(errors);
  });

  return (
    <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={providerMessages}>
      <InitParamsPostMessage errors={errors} />
    </IntlProvider>
  );
}
type InitParamsPostMessageProps = {
  errors: InitParamErrors;
};

export function InitParamsPostMessage({ errors }: InitParamsPostMessageProps) {
  const { formatMessage } = useIntl();
  const logOnrampEventV2 = useLogOnrampEventV2();

  const listOfErrorMessages = useMemo(() => {
    const errorMessages: string[] = [];
    // Note: this is the API first flow so secureInit is required
    if (errors.invalidAppId) {
      errorMessages.push(formatMessage(messages.invalidAppId));
    }

    if (errors.appTierReachedMaxAttempts) {
      errorMessages.push(formatMessage(initParamsErrorMessages.appTierReachedMaxAttempts));
    }

    switch (errors?.appIdAddressesAndSessionToken) {
      case 'all_missing':
        errorMessages.push(formatMessage(messages.missingAppIdAddressesAndSessionToken));
        break;
      case 'secure_init_required':
        errorMessages.push(formatMessage(messages.secureInitRequired));
        break;
      case 'expired_session_token':
        errorMessages.push(formatMessage(messages.expiredSessionToken));
        break;
      case 'invalid_session_token':
        errorMessages.push(formatMessage(messages.expiredSessionToken));
        break;
      case 'app_id_without_addresses':
        errorMessages.push(formatMessage(messages.appIdWithoutAddresses));
        break;
      case 'addresses_without_app_id':
        errorMessages.push(formatMessage(messages.addressesWithoutAppId));
        break;
      case 'client_session_mismatch':
        errorMessages.push(formatMessage(initParamsErrorMessages.clientSessionMismatch));
        break;
      default:
        break;
    }

    if (errorMessages.length === 0) {
      errorMessages.push('Unknown initialization error');
    }

    return errorMessages.join('\n');
  }, [
    errors?.appIdAddressesAndSessionToken,
    errors.invalidAppId,
    errors.appTierReachedMaxAttempts,
    formatMessage,
  ]);

  useEffect(() => {
    const sendInitErrorsPostMessage = () => {
      sendApiOnrampPostMessage({
        eventName: OnrampApiLoadErrorEvent,
        data: {
          errorCode: 'ERROR_CODE_INIT',
          errorMessage: listOfErrorMessages,
        },
      });
    };
    sendInitErrorsPostMessage();
    logOnrampEventV2(API_GCO_APPLE_PAY, {
      ...API_GCO_APPLE_PAY_INIT_PARAMS_ERROR,
      errorMessages: listOfErrorMessages,
    });
  }, [listOfErrorMessages, logOnrampEventV2]);

  return null; // This component does not render anything visible
}

const logInitParamErrorsMetric = wrapWithCallOnce(
  function logInitParamErrorsMetric(errors: InitParamErrors) {
    // Allowed tag characters: https://docs.datadoghq.com/getting_started/tagging/#define-tags
    const formattedTags: Record<string, string | boolean> = {};
    const tagEntries = Object.entries(errors).map(
      ([key, value]) =>
        [snakeCase(key), Array.isArray(value) ? snakeCase(value.join('.')) : value] as const,
    );
    for (const [key, value] of tagEntries) {
      formattedTags[key] = value;
    }

    logMetric({
      metricName: 'gco_api_onramp.apple_pay.init_param_errors',
      metricType: MetricType.count,
      value: 1,
      tags: {
        ...formattedTags,
      },
    });
  },
  {
    argsToKeyFn() {
      return 'init-param-errors';
    },
  },
);

export const messages = defineMessages({
  addressesWithoutAppId: {
    id: 'InitParamsErrorPostMessage.addressesWithoutAppId',
    defaultMessage: 'Missing appID/projectID',
    description:
      'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode.',
  },
  appIdWithoutAddresses: {
    id: 'InitParamsErrorPostMessage.appIdWithoutAddresses',
    defaultMessage: 'Missing address',
    description:
      'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode.',
  },
  destinationWalletsDeprecated: {
    id: 'InitParamsErrorPostMessage.destinationWalletsDeprecated',
    defaultMessage:
      'DestinationWallets is deprecated and has been replaced by addresses and assets',
    description:
      'Warning when a deprecated parameter is provided & an error occurs. Do not translate the contents of inlineCode',
  },
  destinationWalletsWithoutAppId: {
    id: 'InitParamsErrorPostMessage.destinationWalletsWithoutAppId',
    defaultMessage: 'Missing appID/projectID',
    description:
      'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode',
  },
  expiredSessionToken: {
    id: 'InitParamsErrorPostMessage.expiredSessionToken',
    defaultMessage: 'Session token has expired',
    description: 'Error when a one-time-use sessionToken is reused',
  },
  invalidParam: {
    id: 'InitParamsErrorPostMessage.invalidParam',
    defaultMessage: 'Invalid {param}',
    description:
      'Generic error when an initialization parameter is provided with a bad value. Do not translate the contents of inlineCode',
  },
  invalidAppId: {
    id: 'InitParamsErrorPostMessage.invalidAppId',
    defaultMessage: 'Invalid appId: {appId}',
    description:
      'Error when the appId initialization parameter is invalid or missing. Do not translate the contents of inlineCode.',
  },
  missingAppIdAddressesAndSessionToken: {
    id: 'InitParamsErrorPostMessage.missingAppIdAddressesAndSessionToken',
    defaultMessage: 'We require either: (1) appId and addresses, or (2) sessionToken',
    description:
      'Error when all parameters of both options of parameters sets are missing. Do not translate the contents of inlineCode.',
  },
  secureInitRequired: {
    id: 'InitParamsErrorPostMessage.secureInitRequired',
    defaultMessage:
      'Your project is configured to require secure initialization and requires a sessionToken',
    description:
      'Error when the app is configured to require secure initialization and the user is attempting to initialize without it',
  },
});
